import { useEffect } from 'react';
import { Form as AntForm, Input, Modal, message } from 'antd';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, useForm } from 'react-hook-form';
import type { SubmitHandler } from 'react-hook-form';
import { FormActions, FormField } from '../../shared/form';
import {
  UserEmailUpdateSchema,
  type UserEmailUpdateDTO,
} from '../../schemas/user';
import { authFetch } from '../../utils/api';

export interface MemberEmailEditUser {
  id: number;
  brand: string;
  username: string;
  isAnonymous: boolean;
  memberAccountId: number | null;
}

interface MemberEmailEditResponse {
  code: number;
  msg?: string;
  data?: {
    emailMasked?: string;
  } | null;
}

export interface MemberEmailEditModalProps {
  user: MemberEmailEditUser | null;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface SubmitMemberEmailEditParams {
  user: MemberEmailEditUser;
  email: string;
  request: (url: string, init: RequestInit) => Promise<MemberEmailEditResponse>;
  onFieldError: (message: string) => void;
  onGenericError: (message: string) => void;
  onSuccess: (result: MemberEmailEditResponse) => void;
  onReset?: () => void;
  onClose?: () => void;
}

const EMPTY_EMAIL: UserEmailUpdateDTO = { email: '' };

const apiFetch = async (url: string, options?: RequestInit): Promise<MemberEmailEditResponse> => {
  const response = await authFetch(url, options);
  return response.json() as Promise<MemberEmailEditResponse>;
};

export function canEditMemberEmail(
  user: MemberEmailEditUser,
  normalWritesAllowed: boolean,
): boolean {
  return normalWritesAllowed && !user.isAnonymous && user.memberAccountId !== null;
}

/** Select only the fields this modal needs, so a future list projection cannot leak email. */
export function memberEmailEditUserFromRow(user: MemberEmailEditUser): MemberEmailEditUser {
  return {
    id: user.id,
    brand: user.brand,
    username: user.username,
    isAnonymous: user.isAnonymous,
    memberAccountId: user.memberAccountId,
  };
}

/**
 * Isolate the route contract and submit outcomes from the modal rendering.
 * The row intentionally has no email field: the current plaintext email is
 * never prefilled or rendered by this BO control.
 */
export async function submitMemberEmailEdit({
  user,
  email,
  request,
  onFieldError,
  onGenericError,
  onSuccess,
  onReset,
  onClose,
}: SubmitMemberEmailEditParams): Promise<void> {
  try {
    const result = await request(`/api/admin/users/${user.id}/email`, {
      method: 'PUT',
      headers: { 'X-Target-Brand': user.brand },
      body: JSON.stringify({ email }),
    });
    if (result.code === 0) {
      onReset?.();
      onClose?.();
      onSuccess(result);
      return;
    }
    if (result.code === 400 || result.code === 409) {
      onFieldError(result.msg || 'Email 無法更新');
      return;
    }
  } catch {
    // Keep transport and authorization failures free of candidate email data.
  }
  onGenericError('Email 更新失敗，請稍後再試');
}

export const MemberEmailEditModal: React.FC<MemberEmailEditModalProps> = ({
  user,
  open,
  onClose,
  onSuccess,
}) => {
  const form = useForm<UserEmailUpdateDTO>({
    resolver: zodResolver(UserEmailUpdateSchema),
    mode: 'onBlur',
    defaultValues: EMPTY_EMAIL,
  });

  const closeAndReset = () => {
    form.reset(EMPTY_EMAIL);
    onClose();
  };

  useEffect(() => {
    if (open) form.reset(EMPTY_EMAIL);
  }, [form, open]);

  const onSubmit: SubmitHandler<UserEmailUpdateDTO> = async (values) => {
    if (!user || !canEditMemberEmail(user, true)) return;
    await submitMemberEmailEdit({
      user,
      email: values.email,
      request: apiFetch,
      onFieldError: (error) => form.setError('email', { message: error }),
      onGenericError: (error) => message.error(error),
      onReset: () => form.reset(EMPTY_EMAIL),
      onClose,
      onSuccess: (result) => {
        const masked = result.data?.emailMasked;
        message.success(masked ? `Email 已更新：${masked}` : 'Email 已更新');
        onSuccess();
      },
    });
  };

  return (
    <Modal
      title={user ? `修改 Email：${user.username}` : '修改 Email'}
      open={open}
      onCancel={closeAndReset}
      footer={null}
      destroyOnHidden
    >
      <p>目前 Email 不會顯示；請輸入要更新的新 Email。</p>
      <FormProvider {...form}>
        <AntForm layout="vertical" onFinish={form.handleSubmit(onSubmit)}>
          <FormField<UserEmailUpdateDTO>
            name="email"
            label="新 Email"
            required
            control={<Input type="email" autoComplete="email" placeholder="name@example.com" />}
          />
          <FormActions
            onCancel={closeAndReset}
            submitting={form.formState.isSubmitting}
            dirty={form.formState.isDirty}
            submitLabel="更新 Email"
          />
        </AntForm>
      </FormProvider>
    </Modal>
  );
};
